import {flavorDescriptions} from './constants';
import {Coords, EntityType} from './entities/entities.types';

function isNotNull<T>(val: T | null): val is T {
  return val !== null;
}

function printCoords([x, y]: Coords) {
  return `[${x}, ${y}]`;
}

function printCoordsList(coordsList: Array<Coords>) {
  const asString = coordsList.map((coords) => printCoords(coords));
  asString.splice(-1, 0, 'and');
  return asString.join(' ');
}

function describeEntity(entity: EntityType) {
  const flavorDisplayName = flavorDescriptions[entity.flavor].displayName.toLowerCase();
  const article = /^[aeiou]/i.test(flavorDisplayName) ? 'an' : 'a';
  const withFlavor = (description: string) => `${article} ${flavorDisplayName} ${description}`;

  switch (entity.type) {
    case 'point': {
      const {point} = entity.elements;
      return withFlavor(`point plotted at ${printCoords(point)}`);
    }
    case 'line': {
      const {points} = entity.elements;
      return withFlavor(
        `line that passes through ${printCoords(points[0])} and ${printCoords(points[1])}`
      );
    }
    case 'segment': {
      const {points} = entity.elements;
      return withFlavor(
        `segment connecting ${printCoords(points[0])} and ${printCoords(points[1])}`
      );
    }
    case 'ray': {
      const {points} = entity.elements;
      return withFlavor(
        `ray with starting point at ${printCoords(points[0])} that passes through ${printCoords(
          points[1]
        )}`
      );
    }
    case 'polygon': {
      const {points} = entity.elements;
      return withFlavor(`polygon with vertices at ${printCoordsList(points)}`);
    }
    case 'circle': {
      const {center, radiusPoint} = entity.elements;
      return withFlavor(
        `circle with center point at ${printCoords(
          center
        )} and passes through the point ${printCoords(radiusPoint)}`
      );
    }
    case 'parabola': {
      const {vertex, curvePoint} = entity.elements;
      return withFlavor(
        `parabola with vertex at ${printCoords(vertex)} and passes through ${printCoords(
          curvePoint
        )}`
      );
    }
    case 'inequality': {
      const {points} = entity.elements;
      const {inclusive} = entity.attributes;
      const shadeDirection = (() => {
        const [[aX, aY], [bX, bY]] = points;
        if (aX < bX) return 'above';
        if (aX > bX) return 'below';
        if (aY < bY) return 'left of';
        return 'right of';
      })();
      return withFlavor(
        `${inclusive ? 'inclusive' : 'exclusive'} line through ${printCoords(
          points[0]
        )} and ${printCoords(points[1])}; the area ${shadeDirection} the line is shaded`
      );
    }
    default:
      throw new Error('Invalid entity passed to describeEntity.');
  }
}

function describeEntities(entities?: EntityType[]) {
  if (!entities || !entities.length) {
    return '';
  }

  const entityDescriptions = entities.map((entity, i, arr) => {
    const description = describeEntity(entity);
    const isLast = i + 1 === arr.length;
    return `${description}${isLast ? '.' : ','}`;
  });

  return `The graph contains the following elements: ${entityDescriptions.join(' ')}`;
}

export function getAriaDescription({
  boundingBox,
  labels = {},
  xAxisLabel,
  yAxisLabel,
  xTickInterval,
  yTickInterval,
  entities,
  editable
}: any) {
  const [xMin, yMax, xMax, yMin] = boundingBox;

  const {top: labelTop, right: labelRight, bottom: labelBottom, left: labelLeft} = labels;

  const graphDescription = [
    `An ${editable ? 'editable' : 'uneditable'} graph with the x-axis`,
    xAxisLabel ? `labeled "${xAxisLabel}"` : null,
    `that spans from ${xMin} to ${xMax} with an interval of ${xTickInterval}.`,
    `The y-axis`,
    yAxisLabel ? `is labeled "${yAxisLabel}" and` : null,
    `spans from ${yMin} to ${yMax} with an interval of ${yTickInterval}.`
  ]
    .filter(isNotNull)
    .join(' ');

  let sideLabels = [
    labelBottom ? `"${labelBottom}" on the bottom` : null,
    labelLeft ? `"${labelLeft}" on the left` : null,
    labelTop ? `"${labelTop}" on the top` : null,
    labelRight ? `"${labelRight}" on the right` : null
  ].filter(isNotNull);

  if (sideLabels.length > 1) {
    sideLabels = sideLabels.map((val, i, arr) => `${val}${i === arr.length - 1 ? '.' : ','}`);
    sideLabels.splice(-1, 0, 'and');
  }

  const builtSideLabels = sideLabels.length
    ? ['The graph is labeled'].concat(sideLabels).join(' ')
    : '';

  const initialEntitiesDescription = describeEntities(entities);

  const editableEntityInstructions = editable
    ? 'To select editable elements, focus the graph and cycle through the elements using the arrow keys.'
    : null;

  const ariaDescription = [
    graphDescription,
    builtSideLabels,
    initialEntitiesDescription,
    editableEntityInstructions
  ]
    .filter((val) => !!val)
    .join(' ');

  return ariaDescription;
}

export const liveRegionActions = {
  cursorPointPlaced(coords: Coords) {
    return `Cursor point added at ${printCoords(coords)}`;
  },
  cursorPointMoved(coords: Coords) {
    return `Cursor moved to ${printCoords(coords)}`;
  },
  partialInstancePointPlaced(coords: Coords) {
    return `Point placed at ${printCoords(coords)}`;
  },
  elementAdded(entity: EntityType) {
    return `Plotted ${describeEntity(entity)}`;
  },
  elementCanceled() {
    return `Element creation cancelled`;
  },
  elementSelected(entity: EntityType) {
    return `Selected ${describeEntity(entity)}`;
  },
  elementDeleted(entity: EntityType) {
    return `Removed ${describeEntity(entity)}`;
  }
};
